import { Repository } from "./adapters/repository";

import * as retrieveZones from "./useCases/retrieveZones";
import * as retrieveZone from "./useCases/retrieveZone";
import * as removeZone from "./useCases/removeZone";
import * as transferProjects from "./useCases/transferProjects";
import * as createZone from "./useCases/createZone";
import * as editZone from "./useCases/editZone";
import * as retrieveZoneProjects from "./useCases/retrieveZoneProjects";
import * as retrieveZonesProjectsReports from "./useCases/retrieveZonesProjectsReports";
import * as retrieveZonesProjectsGantt from "./useCases/retrieveZonesProjectsGantt";
import * as retrieveZoneGantt from "./useCases/retrieveZoneGantt";
import * as retrieveZonesProjects from "./useCases/retrieveZonesProjects";
import * as retrieveZoneProjectsReports from "./useCases/retrieveZoneProjectsReports";
import * as retrieveCalendar from "./useCases/retrieveCalendar";
import * as retrieveGantt from "./useCases/retrieveGantt";
import * as retrieveZonesIndicators from "./useCases/retrieveZonesIndicators";
import * as retrieveOptimizedGantt from "./useCases/retrieveOptimizedGantt";
import * as retrieveGanttCalendarRange from "./useCases/retrieveGanttCalendarRange";

enum UseCases {
  retrieveZoneGantt = "retrieveZoneGantt",
  retrieveZonesProjectsGantt = "retrieveZonesProjectsGantt",
  retrieveZonesProjectsReports = "retrieveZonesProjectsReports",
  retrieveZones = "retrieveZones",
  retrieveZone = "retrieveZone",
  removeZone = "removeZone",
  transferProjects = "transferProjects",
  createZone = "createZone",
  editZone = "editZone",
  retrieveZoneProjects = "retrieveZoneProjects",
  retrieveZonesProjects = "retrieveZonesProjects",
  retrieveZoneProjectsReports = "retrieveZoneProjectsReports",
  retrieveCalendar = "retrieveCalendar",
  retrieveGantt = "retrieveGantt",
  retrieveZonesIndicators = "retrieveZonesIndicators",
  retrieveOptimizedGantt = "retrieveOptimizedGantt",
  retrieveGanttCalendarRange = "retrieveGanttCalendarRange",
}

interface Core {
  [UseCases.retrieveZoneGantt]: retrieveZoneGantt.Interactor;
  [UseCases.retrieveZonesProjectsGantt]: retrieveZonesProjectsGantt.Interactor;
  [UseCases.retrieveZonesProjectsReports]: retrieveZonesProjectsReports.Interactor;
  [UseCases.retrieveZones]: retrieveZones.Interactor;
  [UseCases.retrieveZone]: retrieveZone.Interactor;
  [UseCases.removeZone]: removeZone.Interactor;
  [UseCases.transferProjects]: transferProjects.Interactor;
  [UseCases.createZone]: createZone.Interactor;
  [UseCases.editZone]: editZone.Interactor;
  [UseCases.retrieveZoneProjects]: retrieveZoneProjects.Interactor;
  [UseCases.retrieveZonesProjects]: retrieveZonesProjects.Interactor;
  [UseCases.retrieveZoneProjectsReports]: retrieveZoneProjectsReports.Interactor;
  [UseCases.retrieveCalendar]: retrieveCalendar.Interactor;
  [UseCases.retrieveGantt]: retrieveGantt.Interactor;
  [UseCases.retrieveZonesIndicators]: retrieveZonesIndicators.Interactor;
  [UseCases.retrieveOptimizedGantt]: retrieveOptimizedGantt.Interactor;
  [UseCases.retrieveGanttCalendarRange]: retrieveGanttCalendarRange.Interactor;
}

type Boundaries = BoundaryMap<Core>;

type Dependencies = { repository: Repository };

function createCore(dependencies: Dependencies): Core {
  return {
    [UseCases.retrieveZones]: retrieveZones.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveZone]: retrieveZone.createInteractor(
      dependencies.repository,
    ),
    [UseCases.removeZone]: removeZone.createInteractor(dependencies.repository),
    [UseCases.transferProjects]: transferProjects.createInteractor(
      dependencies.repository,
    ),
    [UseCases.createZone]: createZone.createInteractor(dependencies.repository),
    [UseCases.editZone]: editZone.createInteractor(dependencies.repository),
    [UseCases.retrieveZoneProjects]: retrieveZoneProjects.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveZonesProjectsReports]:
      retrieveZonesProjectsReports.createInteractor(dependencies.repository),
    [UseCases.retrieveZonesProjectsGantt]:
      retrieveZonesProjectsGantt.createInteractor(dependencies.repository),
    [UseCases.retrieveZoneGantt]: retrieveZoneGantt.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveZonesProjects]: retrieveZonesProjects.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveZoneProjectsReports]:
      retrieveZoneProjectsReports.createInteractor(dependencies.repository),
    [UseCases.retrieveCalendar]: retrieveCalendar.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveGantt]: retrieveGantt.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveZonesIndicators]:
      retrieveZonesIndicators.createInteractor(dependencies.repository),
    [UseCases.retrieveOptimizedGantt]: retrieveOptimizedGantt.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveGanttCalendarRange]:
      retrieveGanttCalendarRange.createInteractor(dependencies.repository),
  };
}

export type { Core, Boundaries, Dependencies };

export { UseCases, createCore };
